/**
 * useAdminAuth — verifica daca device-ul curent are rol admin/super_admin.
 *
 * Flow:
 *  - La mount, citeste role din visitors (cu cache local pentru performanta)
 *  - Expune login(role) si logout()
 *  - Pastreaza un cache local (localStorage) cu TTL scurt ca sa nu bombardam Supabase
 */

import { useCallback, useEffect, useState } from 'react';
import {
  type AdminRole,
  checkPin,
  clearMyRole,
  getMyRole,
  setMyRole,
} from '@/lib/admin';

const CACHE_KEY = 'guestlens_admin_role';
const CACHE_TTL_MS = 60_000; // 1 minut — re-citim frecvent ca delogarea sa se propage

interface CacheEntry {
  role: AdminRole | null;
  expires: number;
}

function readCache(): CacheEntry | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (typeof parsed.expires !== 'number' || parsed.expires < Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(role: AdminRole | null) {
  if (typeof window === 'undefined') return;
  const entry: CacheEntry = { role, expires: Date.now() + CACHE_TTL_MS };
  try {
    if (role === null) {
      localStorage.removeItem(CACHE_KEY);
    } else {
      localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    }
  } catch {
    // localStorage plin / disabled - ignoram
  }
}

export function useAdminAuth() {
  const [role, setRole] = useState<AdminRole | null>(() => readCache()?.role ?? null);
  const [loading, setLoading] = useState(true);

  // La mount: citeste de la server (daca nu avem cache proaspat)
  useEffect(() => {
    const cached = readCache();
    if (cached) {
      setRole(cached.role);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const r = await getMyRole();
        if (!cancelled) {
          setRole(r);
          writeCache(r);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[useAdminAuth] initial fetch failed:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const loginWithPin = useCallback(async (input: string): Promise<AdminRole | null> => {
    const r = checkPin(input);
    if (!r) return null;

    try {
      await setMyRole(r);
      setRole(r);
      writeCache(r);
      return r;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useAdminAuth] setRole failed:', e);
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await clearMyRole();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useAdminAuth] clearMyRole failed:', e);
    } finally {
      setRole(null);
      writeCache(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await getMyRole();
      setRole(r);
      writeCache(r);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useAdminAuth] refresh failed:', e);
    }
  }, []);

  return { role, loading, loginWithPin, logout, refresh };
}
